"use client"

import { useMemo, useState } from "react"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { TOOL_METADATA } from "@/lib/tool-metadata"

const FALLBACK_RELATED = [
  "freezer-btu-calculator",
  "display-cooler-calculator",
  "energy-cost-calculator",
  "appliance-energy-calculator",
]

function buildRelated(slugs: string[], currentSlug: string) {
  const seen = new Set<string>([currentSlug])
  const out: { title: string; category: string; description: string; href: string; time: string }[] = []
  for (const s of [...slugs, ...FALLBACK_RELATED]) {
    if (out.length === 4) break
    if (seen.has(s)) continue
    const m = TOOL_METADATA[s]
    if (!m) continue
    seen.add(s)
    out.push({
      title: m.name,
      category: m.category,
      description: m.shortDescription,
      href: `/tools/${s}`,
      time: "~30 sec",
    })
  }
  return out
}

const META = {
  name: "Walk-In Cooler Calculator",
  category: "Commercial" as const,
  mediumDescription:
    "Spec a walk-in cooler condensing unit using ASHRAE-style heat-load math: surface conduction, air infiltration, and product load — with a 25% safety factor on top. Sized in BTU/hr at the design temperature you enter.",
  faqs: [
    { question: "What BTU does my walk-in cooler need?", answer: "It depends on volume, target temperature, ambient temperature, and how often the door opens. A typical 10×8×8 ft cooler at 35°F in a 75°F room needs roughly 8,000–14,000 BTU/hr — but always size with the actual heat-load calc." },
    { question: "Why include a 25% safety factor?", answer: "Refrigeration design practice (ASHRAE) recommends a 10–25% safety margin. It accounts for door opening losses, defrost recovery, hot product loading, and equipment aging. Undersized units run continuously and burn out compressors." },
    { question: "What insulation R-value do walk-ins need?", answer: "R-25 is standard for cooler temperatures (33–40°F). R-30 or higher is recommended for freezer temperatures (below 32°F). Polyurethane foam panels at 4 inches typically deliver R-25 to R-32 depending on density." },
    { question: "How many air changes per hour should I assume?", answer: "Low traffic (storage cooler, few openings/day): 1 ACH. Medium traffic (kitchen prep): 2 ACH. High traffic (restaurant line, retail walk-in): 4 ACH. Higher traffic dramatically increases load." },
    { question: "What's the difference between a cooler and a freezer condensing unit?", answer: "Coolers (≥32°F) use medium-temp R-448A or R-449A condensing units. Freezers (<32°F) use low-temp condensing units, heated door frames to prevent ice, and require thicker insulation (R-30+)." },
    { question: "Should I use one big condensing unit or multiple small ones?", answer: "Above ~500 cu ft, multiple evaporators are usually recommended for even temperature and redundancy. A single failure on a one-evaporator system means total loss; a two-evaporator system can hold temp on one." },
  ],
  relatedTools: ["display-cooler-calculator", "freezer-btu-calculator", "energy-cost-calculator"],
}

export function WalkInCoolerCalculatorPageClient() {
  const [length, setLength] = useState("10")
  const [width, setWidth] = useState("8")
  const [height, setHeight] = useState("8")
  const [targetTemp, setTargetTemp] = useState("35")
  const [ambientTemp, setAmbientTemp] = useState("75")
  const [usage, setUsage] = useState("medium")

  // Original calc logic preserved verbatim from the legacy page-client.
  const result = useMemo(() => {
    const l = parseFloat(length)
    const w = parseFloat(width)
    const h = parseFloat(height)
    const target = parseFloat(targetTemp)
    const ambient = parseFloat(ambientTemp)

    if (!l || !w || !h || isNaN(target) || isNaN(ambient)) return null

    const cubicFeet = l * w * h
    const wallArea = 2 * (l * h) + 2 * (w * h)
    const ceilingFloorArea = 2 * (l * w)
    const totalArea = wallArea + ceilingFloorArea
    const tempDiff = ambient - target

    const heatTransferFactor = 1.5
    const surfaceBTU = totalArea * tempDiff * heatTransferFactor

    const airChangesPerHour = usage === "high" ? 4 : usage === "medium" ? 2 : 1
    const airInfiltrationBTU = cubicFeet * airChangesPerHour * 1.08 * tempDiff
    const productLoadBTU = cubicFeet * 10

    let btuRequired = surfaceBTU + airInfiltrationBTU + productLoadBTU
    btuRequired = Math.ceil(btuRequired * 1.25)

    let recommendedUnit = ""
    if (btuRequired < 5000) recommendedUnit = "1/3 HP condensing unit (4,000–5,000 BTU/hr)"
    else if (btuRequired < 8000) recommendedUnit = "1/2 HP condensing unit (6,000–8,000 BTU/hr)"
    else if (btuRequired < 12000) recommendedUnit = "3/4 HP condensing unit (9,000–12,000 BTU/hr)"
    else if (btuRequired < 18000) recommendedUnit = "1 HP condensing unit (12,000–18,000 BTU/hr)"
    else if (btuRequired < 24000) recommendedUnit = "1.5 HP condensing unit (18,000–24,000 BTU/hr)"
    else recommendedUnit = "2+ HP condensing unit (24,000+ BTU/hr)"

    const insulationR = target < 0 ? 30 : target < 35 ? 25 : 20
    const watts = btuRequired * 0.293
    const amps = Math.ceil(watts / 240)
    const power = `${amps} A @ 240V (~${Math.ceil(watts / 1000)} kW)`

    return {
      cubicFeet: Math.round(cubicFeet),
      btuRequired,
      recommendedUnit,
      insulationR,
      power,
      airChangesPerHour,
    }
  }, [length, width, height, targetTemp, ambientTemp, usage])

  return (
    <ToolPageShell
      toolName={META.name}
      heroEyebrow={META.category}
      heroTitlePre="Spec the walk-in"
      heroTitleItalic="condensing"
      heroTitlePost="unit."
      heroSubtitle={META.mediumDescription}
      heroMeta={["⏱ ~45 sec", "ASHRAE-style heat-load math"]}
      heroShare={<ToolShareBar title={META.name} />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: "+25%", label: "Safety factor", note: "ASHRAE" },
            { value: "R-25", label: "Cooler insulation", note: "Min" },
            { value: "R-30", label: "Freezer insulation", note: "Below 32°F" },
            { value: "1.08", label: "Air infiltration", note: "BTU/cf/°F/ACH" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={META.faqs}
      relatedTools={buildRelated(META.relatedTools, "walk-in-cooler-calculator")}
      cta={{
        title: "Browse commercial-grade refrigeration.",
        body: "Browse bottom-freezer and side-by-side refrigerators — useful starting points if you're spec'ing residential reach-ins alongside your walk-in.",
        buttonLabel: "Browse bottom-freezer fridges",
        buttonHref: "/collections/bottom-freezer-refrigerators",
      }}
    >
      {/* ============ CALCULATOR ============ */}
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">
                  STEP 01
                </span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}
              >
                Box dimensions,
                <br />
                temps, traffic.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                Dimensions in feet. Temperatures in °F. Traffic determines air changes per hour.
              </p>

              <div className="mt-7 space-y-5">
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                      Length (ft)
                    </label>
                    <input
                      type="number"
                      step="0.5"
                      value={length}
                      onChange={(e) => setLength(e.target.value)}
                      className="tool-input-clean w-full px-3 py-3 text-[16px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                      Width (ft)
                    </label>
                    <input
                      type="number"
                      step="0.5"
                      value={width}
                      onChange={(e) => setWidth(e.target.value)}
                      className="tool-input-clean w-full px-3 py-3 text-[16px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                      Height (ft)
                    </label>
                    <input
                      type="number"
                      step="0.5"
                      value={height}
                      onChange={(e) => setHeight(e.target.value)}
                      className="tool-input-clean w-full px-3 py-3 text-[16px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                      Target temp (°F)
                    </label>
                    <input
                      type="number"
                      value={targetTemp}
                      onChange={(e) => setTargetTemp(e.target.value)}
                      className="tool-input-clean w-full px-3 py-3 text-[16px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                      Ambient (°F)
                    </label>
                    <input
                      type="number"
                      value={ambientTemp}
                      onChange={(e) => setAmbientTemp(e.target.value)}
                      className="tool-input-clean w-full px-3 py-3 text-[16px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Door traffic
                  </label>
                  <select
                    value={usage}
                    onChange={(e) => setUsage(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="low">Low (1 air change / hr)</option>
                    <option value="medium">Medium (2 air changes / hr)</option>
                    <option value="high">High (4 air changes / hr)</option>
                  </select>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> Restaurant line is high; back-of-house storage is low.
                  </div>
                </div>
                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you type
                </div>
              </div>
            </div>

            {/* Result panel */}
            <div className="flex flex-col">
              <div
                className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1"
                style={{ background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)" }}
              >
                <div
                  aria-hidden
                  className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none"
                  style={{
                    background: "rgba(255,153,81,0.35)",
                    filter: "blur(60px)",
                    transform: "translate(25%, -35%)",
                  }}
                />
                <div className="relative">
                  <div className="flex items-center justify-between mb-8 flex-wrap gap-2">
                    <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70">
                      Required cooling capacity
                    </div>
                    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] text-white/60">
                      <RefreshCw className="w-3 h-3" /> Updated live
                    </div>
                  </div>
                  <div
                    className="font-black"
                    style={{
                      fontSize: "clamp(44px, 6.5vw, 80px)",
                      letterSpacing: "-0.045em",
                      lineHeight: 0.9,
                    }}
                  >
                    {result?.btuRequired?.toLocaleString() ?? 0}
                    <span className="text-[18px] md:text-[22px] font-black tracking-tighter text-white/60 ml-2">
                      BTU/hr
                    </span>
                  </div>
                  <div className="mt-4 text-[14px] text-white/80 leading-[1.5]">
                    {result?.recommendedUnit ?? "Enter your box dimensions and target temp."}
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-6 md:mt-8">
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">
                        Interior volume
                      </div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">
                        {result?.cubicFeet ?? "—"} cu ft
                      </div>
                    </div>
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">
                        Insulation
                      </div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">
                        R-{result?.insulationR ?? "—"}
                      </div>
                    </div>
                    <div className="col-span-2 rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">
                        Power requirement
                      </div>
                      <div className="text-[16px] sm:text-[18px] font-black tracking-tight">
                        {result?.power ?? "—"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FIRST CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · Reach-in references"
          title="Bottom-freezer fridges for back-of-house"
          description="Useful spec references for residential-style reach-ins next to a walk-in cooler."
          collectionType="bottom-freezer"
          fallbackCollectionType="french-door"
        />
      </section>

      {/* ============ METHODOLOGY ============ */}
      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">
                Under the hood
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}
              >
                <em className="italic text-[#ff9951]">ASHRAE-style</em> heat-load with safety margin.
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                Three load components: surface conduction through walls, air infiltration on door open, and product load from stored items. 25% safety factor on top.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                {
                  n: "01",
                  t: "Surface conduction",
                  d: "Total surface area × (ambient − target) × 1.5 heat-transfer factor (assumes R-25 insulation).",
                },
                {
                  n: "02",
                  t: "Air infiltration",
                  d: "Volume × ACH × 1.08 × ΔT. ACH = 1 (low traffic), 2 (medium), 4 (high traffic).",
                },
                {
                  n: "03",
                  t: "Product load",
                  d: "Approximated as 10 BTU/hr per cubic foot of interior volume — typical for stored prepped food.",
                },
                {
                  n: "04",
                  t: "Safety factor",
                  d: "Sum × 1.25 for door opening losses, defrost recovery, hot product loading, and equipment aging.",
                },
              ].map((s) => (
                <li
                  key={s.n}
                  className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900"
                >
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">
                    {s.n}
                  </div>
                  <div>
                    <h3
                      className="text-gray-900"
                      style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}
                    >
                      {s.t}
                    </h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ============ SECOND CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Side-by-side fridges for prep stations"
          description="Eye-level freezer for quick access — common in commercial kitchen prep areas."
          collectionType="side-by-side"
          fallbackCollectionType="bottom-freezer"
        />
      </section>
    </ToolPageShell>
  )
}
